import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Gavel, ScrollText, ShieldCheck, Users2, FileText,
  Wallet, FolderArchive, ClipboardList, Sparkles, CheckCircle2, User,
} from "lucide-react";

const FEATURES = [
  { icon: Users2, title: "Clientes", body: "Fichas completas de personas físicas y jurídicas con historial de servicios, contactos y honorarios." },
  { icon: Gavel, title: "Procesos", body: "Expedientes judiciales con partes, plazos, resoluciones, audiencias y notificaciones." },
  { icon: ScrollText, title: "Notarial", body: "Escrituras, protocolos, tomos e índices — con búsqueda avanzada y control notarial completo." },
  { icon: ClipboardList, title: "Solicitudes", body: "Portal para que clientes envíen requerimientos con flujo de aprobación y bitácora." },
  { icon: FileText, title: "Formularios", body: "Formularios dinámicos para clientes, contratos y trámites, con validaciones y firma digital." },
  { icon: Wallet, title: "Cobros", body: "Honorarios, facturación, estados de cuenta y reportes mensuales al detalle." },
  { icon: FolderArchive, title: "Archivo", body: "Repositorio central de expedientes y documentos con permisos por rol." },
  { icon: User, title: "Perfiles", body: "Gestión de usuarios y roles: Super Administrador · Administrador · Asistente." },
  { icon: ShieldCheck, title: "Seguridad y auditoría", body: "Cifrado de sesiones, 2FA opcional y audit log de acciones críticas." },
];

const NAV = [
  { label: "Módulos", href: "#modulos" },
  { label: "Beneficios", href: "#beneficios" },
  { label: "Precios", href: "#precios" },
  { label: "FAQ", href: "#faq" },
  { label: "Contáctanos", href: "#contacto" },
];

export default function Landing() {
  return (
    <div className="min-h-screen paper text-[#1A0810]">
      {/* Header */}
      <header className="relative z-10 border-b border-[#E5DFD3]/70 bg-[#F1EDE4]/80 backdrop-blur-md sticky top-0">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 h-20 flex items-center justify-between">
          <Logo variant="dark" />
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="text-sm text-[#3d2a2a] hover:text-[#7A1E2E] transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-link">
              <Button variant="outline" className="border-[#2B0913]/20 bg-transparent hover:bg-[#2B0913]/5 text-[#2B0913]">
                Iniciar sesión
              </Button>
            </Link>
            <Link to="/register" data-testid="landing-register-link">
              <Button className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
                Crear cuenta <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5DFD3] px-3 py-1 text-xs text-[#3d2a2a]">
              <Sparkles className="h-3 w-3 text-[#C25A5A]" strokeWidth={2} /> Sistema para Despacho Legal · Costa Rica
            </div>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] text-[#1A0810]">
              Su despacho, <span className="text-[#7A1E2E]">en orden.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[#4a3838]">
              Gestión integral de casos, procesos judiciales, notarial, clientes y cobros para bufetes costarricenses.
              Precisión, discreción y control — todo en un solo lugar.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 max-w-xl">
              {[
                "Expedientes judiciales completos",
                "Control notarial y protocolos",
                "Facturación y cobros",
                "Firma y archivo digital",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-[#2B0913]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C25A5A]" />{t}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-cta-register">
                <Button className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4] h-11 px-6">
                  Comenzar ahora <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
                </Button>
              </Link>
              <a href="#modulos">
                <Button variant="outline" className="h-11 px-6 border-[#2B0913]/20 bg-transparent text-[#2B0913] hover:bg-[#2B0913]/5">
                  Ver módulos
                </Button>
              </a>
            </div>
          </div>

          {/* Right card mimicking sister's "PLANILLA · SEMANA ACTUAL" */}
          <div className="animate-fade-up relative" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-6 bg-gradient-to-br from-[#7A1E2E]/10 to-[#C19B6C]/10 rounded-3xl blur-2xl" />
            <div className="relative rounded-2xl bg-white border border-[#E5DFD3] shadow-[0_20px_60px_-20px_rgba(43,9,19,0.25)] p-6">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#6B5D57]">
                <span>Expedientes · Esta semana</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF6EE] text-[#1F5B2E] px-2 py-0.5 text-[10px] normal-case tracking-normal">
                  <CheckCircle2 className="h-3 w-3" strokeWidth={2} /> Al día
                </span>
              </div>
              <div className="mt-5 divide-y divide-[#E5DFD3]">
                {[
                  { name: "Rojas c/ Ministerio", tag: "Preliminar", amount: "₡ 850.000" },
                  { name: "Solano vs. Aseguradora", tag: "Conciliación", amount: "₡ 425.000" },
                  { name: "Vega c/ Empresa X", tag: "Juicio oral", amount: "₡ 1.240.000" },
                  { name: "Compraventa Finca 12345", tag: "Escritura pública", amount: "₡ 680.000" },
                ].map((row) => (
                  <div key={row.name} className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-sm font-medium text-[#1A0810]">{row.name}</div>
                      <div className="text-[11px] uppercase tracking-widest text-[#6B5D57] mt-0.5">{row.tag}</div>
                    </div>
                    <div className="font-display text-base text-[#7A1E2E]">{row.amount}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-[#E5DFD3] flex items-center justify-between">
                <span className="text-sm text-[#6B5D57]">Total honorarios:</span>
                <span className="font-display text-lg text-[#1A0810]">₡ 3.195.000</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section id="beneficios" className="border-t border-[#E5DFD3]/70">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 py-20 lg:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Por qué elegirnos</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A0810]">
              Menos papeleo, más tiempo para litigar
            </h2>
            <p className="mt-4 text-[#4a3838]">
              Diseñado desde cero para bufetes costarricenses que valoran precisión, cumplimiento y elegancia.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Hecho para Costa Rica", body: "Adaptado al Código Procesal Civil, Notarial y a la realidad de despachos locales." },
              { title: "Interfaz elegante", body: "Rápida, limpia y pensada para el trabajo diario del abogado — sin fricción." },
              { title: "Seguridad y auditoría", body: "Roles finos, bitácora de acciones críticas y cifrado de sesiones." },
              { title: "Reportes y facturación", body: "Estados de cuenta, honorarios, exportación PDF/Excel y envío por correo." },
            ].map((b) => (
              <div key={b.title} className="rounded-xl bg-white border border-[#E5DFD3] p-6 hover:shadow-[0_10px_30px_-10px_rgba(43,9,19,0.15)] transition-shadow">
                <div className="font-display text-lg">{b.title}</div>
                <p className="mt-2 text-sm text-[#4a3838] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Módulos */}
      <section id="modulos" className="border-t border-[#E5DFD3]/70 bg-[#EAE4D5]/40">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 py-20 lg:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Módulos</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A0810]">
              9 módulos que cubren todo el ciclo del despacho
            </h2>
            <p className="mt-4 text-[#4a3838]">
              Desde la primera consulta hasta el archivo final del expediente — sin sistemas paralelos.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="group rounded-xl bg-white border border-[#E5DFD3] p-6 hover:border-[#7A1E2E]/40 hover:shadow-[0_10px_30px_-10px_rgba(43,9,19,0.15)] transition-all animate-fade-up"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className="h-10 w-10 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center group-hover:border-[#C19B6C] transition-colors">
                  <f.icon className="h-5 w-5 text-[#7A1E2E]" strokeWidth={1.6} />
                </div>
                <div className="mt-5 font-display text-lg text-[#1A0810]">{f.title}</div>
                <p className="mt-2 text-sm text-[#4a3838] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contacto" className="border-t border-[#E5DFD3]/70">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 py-20 lg:py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A0810]">
            Empiece hoy a gestionar su despacho como debe ser.
          </h2>
          <p className="mt-4 text-[#4a3838] max-w-2xl mx-auto">
            Sin instalaciones. Con soporte real. Diseñado para Costa Rica.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <Link to="/register" data-testid="cta-register">
              <Button className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4] h-11 px-6">
                Crear cuenta <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2} />
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-login">
              <Button variant="outline" className="h-11 px-6 border-[#2B0913]/20 bg-transparent text-[#2B0913] hover:bg-[#2B0913]/5">
                Ya tengo cuenta
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5DFD3]/70">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 py-8 flex items-center justify-between text-xs text-[#6B5D57]">
          <span>© {new Date().getFullYear()} Grupo Vértice · Despacho Legal</span>
          <span>San José, Costa Rica</span>
        </div>
      </footer>
    </div>
  );
}
